// ===========================================
// CHIP ANIMATION UTILITIES
// ===========================================
// Consolidated animation functions for all chip movements
// Uses WAAPI with viewport coordinates per CHIP_ANIMATION_TECHNIQUE.md
// ===========================================
//
// MIGRATION STATUS:
// -----------------
// The main table (/table/:tableId) now uses PixiTablePage with Pixi-based
// animations (PixiGameTableV1.tsx). This file contains DOM-based animations
// used by:
//   1. Solo mode (SoloGameWindow.tsx) - still uses DOM animations
//   2. useGameEvents.ts - opponent animations for multiplayer
//   3. Legacy TablePage.tsx (MARKED FOR DELETION)
//
// KEEP:
//   - breakdownChips, capChipsForAnimation, CHIP_SIZES (utilities)
//   - animateOpponent* functions (used by useGameEvents for multiplayer)
//   - animateRailToInsuranceKitty, animateInsurance* (used by Solo mode)
//
// MARKED FOR DELETION (after Solo mode migrates to Pixi):
//   - animatePayoutToKitty, animateKittyToRail, animateKittyToDealer
//   - animateChipBatch, animateRailToKitty, animateStagedToKitty
//   - waitForElement, waitForElements, ChipUnstackTracker
// ===========================================

import { playSound } from './animationRunner';
import { CHIP_IMAGES } from '../assets/themes/table2/chips';
import { formatBalance } from './format';
// clearStoredCards import removed - card clones now persist until rake at BET_CONFIRMED

// Import and re-export breakdownChips from shared (single source of truth)
import { breakdownChips as sharedBreakdownChips } from '@busted-blackjack/shared';
export const breakdownChips = sharedBreakdownChips;

// ===========================================
// CONSTANTS
// ===========================================

// Note: CHIP_DENOMINATIONS moved to shared/animation-timing.ts

/** Sound volume multiplier for opponent chip animations (0.0-1.0) */
const OPPONENT_SOUND_VOLUME = 0.4;

// Canvas dimensions (must match GameTable.tsx)
const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;
const HEADER_HEIGHT = 56;

/**
 * Get the current game table scale factor.
 * The canvas is CSS-scaled to fit the viewport.
 *
 * Priority:
 * 1. Explicit data-game-scale attribute on animation layer
 * 2. Auto-detect from animation layer dimensions (offsetWidth vs getBoundingClientRect)
 * 3. Fallback: calculate scale for 1920x1080 multiplayer canvas
 */
function getGameTableScale(): number {
  const animLayer = document.querySelector('[data-chip-animation-layer]') as HTMLElement | null;

  // Check for explicit scale override
  if (animLayer?.dataset.gameScale) {
    return parseFloat(animLayer.dataset.gameScale);
  }

  // Auto-detect scale from animation layer dimensions
  // offsetWidth = unscaled CSS width, getBoundingClientRect().width = scaled visual width
  // This handles both responsive-scaled solo mode and unscaled multiplayer mode
  if (animLayer) {
    const cssWidth = animLayer.offsetWidth;
    const visualWidth = animLayer.getBoundingClientRect().width;
    if (cssWidth > 0 && visualWidth > 0) {
      return visualWidth / cssWidth;
    }
  }

  // Fallback: calculate scale for 1920x1080 multiplayer canvas
  const scaleX = window.innerWidth / CANVAS_WIDTH;
  const scaleY = (window.innerHeight - HEADER_HEIGHT) / CANVAS_HEIGHT;
  return Math.min(scaleX, scaleY);
}

/** Maximum visible chips per denomination in a stack (must match ChipStack.tsx) */
const MAX_VISIBLE_PER_DENOM = 3;

/** Standard chip sizes in pixels (must match CSS classes in components) */
export const CHIP_SIZES = {
  dealerPile: 20,   // w-5 h-5 in DealerChipPile tray slots
  dealerPilePop: 25, // 25% larger for departing animations (visual pop)
  kittyMedium: 28,  // w-7 h-7 in ChipStack (medium size for current user)
  kittySmall: 20,   // w-5 h-5 in ChipStack (small size for other players)
  rail: 48,         // w-12 h-12 in ChipRailButton
  staged: 24,       // w-6 h-6 in staged chips display
};

/** Chip colors by denomination (hex values) - exported for direct DOM manipulation */
export const CHIP_COLORS_HEX: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: '#e5e7eb', border: '#9ca3af', text: '#1f2937' },
  5: { bg: '#ef4444', border: '#b91c1c', text: '#ffffff' },
  25: { bg: '#22c55e', border: '#15803d', text: '#ffffff' },
  100: { bg: '#111827', border: '#4b5563', text: '#ffffff' },
  500: { bg: '#9333ea', border: '#6b21a8', text: '#ffffff' },
  1000: { bg: '#f97316', border: '#c2410c', text: '#ffffff' },
  5000: { bg: '#b45309', border: '#78350f', text: '#ffffff' },
  10000: { bg: '#ec4899', border: '#be185d', text: '#ffffff' },
  100000: { bg: '#06b6d4', border: '#0e7490', text: '#ffffff' },
  1000000: { bg: '#facc15', border: '#ca8a04', text: '#111827' },
};

// ===========================================
// HELPERS
// ===========================================

// breakdownChips is now imported from shared package (see top of file)

/**
 * Maximum number of chips to animate per denomination.
 * This prevents performance issues with large amounts.
 * Any denomination exceeding this count will be consolidated.
 */
const MAX_CHIPS_PER_DENOM = 100;

/**
 * Result of capping chips for animation.
 * Contains the capped chip array and adjusted per-chip values for consolidated denominations.
 */
export interface CappedChipsResult {
  /** Chip denominations to animate (each denom capped at MAX_CHIPS_PER_DENOM) */
  chips: number[];
  /**
   * Adjusted counter value for each denomination that was capped.
   * Key = original denomination, Value = adjusted per-chip value.
   * Denominations not in this map use their face value.
   */
  adjustedValues: Map<number, number>;
  /** @deprecated Use adjustedValues.get(10000) ?? 10000 instead */
  tenKValue: number;
}

/**
 * Caps chips for animation to prevent performance issues with large amounts.
 *
 * This function caps ANY denomination at 100 chips, adjusting the per-chip
 * counter value so the total still reaches the correct amount.
 *
 * Example: $2,000,000 = 200 x $10k chips
 *   - Animated: 100 chips
 *   - Each chip increments counter by: $2,000,000 / 100 = $20,000
 *
 * Example: 500 x $1 chips (hypothetical edge case)
 *   - Animated: 100 chips
 *   - Each chip increments counter by: $500 / 100 = $5
 *
 * @param chips - Original chip array from breakdownChips()
 * @returns Capped chips and adjusted values for consolidated denominations
 */
export function capChipsForAnimation(chips: number[]): CappedChipsResult {
  // Group chips by denomination
  const denomCounts = new Map<number, number>();
  for (const denom of chips) {
    denomCounts.set(denom, (denomCounts.get(denom) || 0) + 1);
  }

  const cappedChips: number[] = [];
  const adjustedValues = new Map<number, number>();
  let needsCapping = false;

  // Process each denomination
  for (const [denom, count] of denomCounts) {
    if (count <= MAX_CHIPS_PER_DENOM) {
      // No capping needed for this denomination
      for (let i = 0; i < count; i++) {
        cappedChips.push(denom);
      }
    } else {
      // Cap this denomination and calculate adjusted per-chip value
      needsCapping = true;
      const totalAmount = count * denom;
      const adjustedValue = totalAmount / MAX_CHIPS_PER_DENOM;
      adjustedValues.set(denom, adjustedValue);

      for (let i = 0; i < MAX_CHIPS_PER_DENOM; i++) {
        cappedChips.push(denom);
      }
    }
  }

  // Sort to maintain denomination order (largest first)
  cappedChips.sort((a, b) => b - a);

  // Calculate tenKValue for backwards compatibility
  const tenKValue = adjustedValues.get(10000) ?? 10000;

  if (!needsCapping) {
    return {
      chips,
      adjustedValues: new Map(),
      tenKValue: 10000,
    };
  }

  return {
    chips: cappedChips,
    adjustedValues,
    tenKValue,
  };
}

/**
 * Get chip colors for a denomination
 */
function getChipColors(denom: number) {
  return CHIP_COLORS_HEX[denom] || CHIP_COLORS_HEX[1];
}

/**
 * Tracks chip unstacking for "chips leaving" animations.
 *
 * When multiple chips of a denomination animate out (e.g., 8 × ฿1 chips),
 * only the last MAX_VISIBLE_PER_DENOM chips should cause visual changes.
 *
 * Example with 8 chips (3 visible):
 * - Chips 1-5 animate away → no visual change (remaining 7,6,5,4,3 all > 3)
 * - Chip 6 animates away → hide visual chip index 2 (remaining = 3)
 * - Chip 7 animates away → hide visual chip index 1 (remaining = 2)
 * - Chip 8 animates away → hide visual chip index 0 (remaining = 1)
 */
/**
 * Tracks chip unstacking for "chips leaving" animations.
 *
 * When multiple chips of a denomination animate out (e.g., 8 × ฿1 chips),
 * only the last MAX_VISIBLE_PER_DENOM chips should cause visual changes.
 *
 * Example with 8 chips (3 visible):
 * - Chips 1-5 animate away → no visual change (remaining 7,6,5,4,3 all > 3)
 * - Chip 6 animates away → hide visual chip index 2 (remaining = 3)
 * - Chip 7 animates away → hide visual chip index 1 (remaining = 2)
 * - Chip 8 animates away → hide visual chip index 0 (remaining = 1)
 */
export class ChipUnstackTracker {
  private totalCounts: Map<number, number>;
  private animatedCounts: Map<number, number>;
  private maxVisible: number;

  constructor(chips: number[], maxVisible = MAX_VISIBLE_PER_DENOM) {
    this.maxVisible = maxVisible;
    this.totalCounts = new Map();
    this.animatedCounts = new Map();

    // Count total chips per denomination
    for (const chip of chips) {
      this.totalCounts.set(chip, (this.totalCounts.get(chip) || 0) + 1);
    }
  }

  /**
   * Get the visual chip index to hide, or null if no visual chip should be hidden yet.
   * Call this BEFORE recording the animation start.
   */
  getChipIndexToHide(denom: number): number | null {
    const total = this.totalCounts.get(denom) || 0;
    const animated = this.animatedCounts.get(denom) || 0;
    const remaining = total - animated;

    // Only hide visual chips when remaining count drops to maxVisible or below
    if (remaining <= this.maxVisible) {
      // Hide in reverse order: highest index first
      // remaining=3 → hide index 2
      // remaining=2 → hide index 1
      // remaining=1 → hide index 0
      return remaining - 1;
    }

    return null; // No visual chip to hide yet
  }

  /**
   * Record that a chip animation has started.
   * Call this AFTER getting the chip index to hide.
   */
  recordAnimationStart(denom: number): void {
    this.animatedCounts.set(denom, (this.animatedCounts.get(denom) || 0) + 1);
  }
}

/**
 * Format chip label (e.g., 1000 -> "1K")
 */
function formatChipLabel(value: number): string {
  return value >= 1000 ? `${value / 1000}K` : value.toString();
}

// ===========================================
// CORE ANIMATION FUNCTION
// ===========================================

export interface ChipFlightConfig {
  /** Chip denomination value */
  value: number;
  /** Source bounding rect */
  sourceRect: DOMRect;
  /** Target bounding rect */
  targetRect: DOMRect;
  /** Starting chip size in pixels */
  sourceChipSize: number;
  /** Ending chip size in pixels */
  targetChipSize: number;
  /** Animation duration in ms (default: 200) */
  duration?: number;
  /** Easing function (default: 'ease-out') */
  easing?: string;
  /** Z-index for the flying chip (default: 9999). Use lower value to animate behind avatars. */
  zIndex?: number;
  /** Sound volume multiplier 0.0-1.0 (default: 1.0). Use lower values for opponent chips. */
  soundVolume?: number;
}

/**
 * Core animation: Animate a single chip from source to target
 * Creates a temporary DOM element, animates it with WAAPI, then removes it.
 * Returns a promise that resolves when animation completes.
 *
 * If a chip animation layer ([data-chip-animation-layer]) exists, uses absolute
 * positioning relative to it. This prevents chips from "escaping" the container
 * during rapid scrolling. Falls back to document.body with fixed positioning.
 */
export function animateChipFlight(config: ChipFlightConfig): Promise<void> {
  return new Promise((resolve) => {
    const {
      value,
      sourceRect,
      targetRect,
      sourceChipSize,
      targetChipSize,
      duration = 200,
      easing = 'ease-out',
      zIndex = 9999,
      soundVolume = 1.0,
    } = config;

    const colors = getChipColors(value);

    // Use source size for the flying chip
    const flyingChipSize = sourceChipSize;

    // Scale font and border proportionally (base ratio: 14px/48px, 4px/48px)
    const fontSize = flyingChipSize * 0.29;
    const borderWidth = flyingChipSize * 0.083;

    // Check for chip animation layer - use container-relative positioning if available
    const chipAnimLayer = document.querySelector('[data-chip-animation-layer]') as HTMLElement | null;
    const useContainerRelative = chipAnimLayer !== null;

    // Calculate scale directly from chipAnimLayer (same approach as animateChipFlightInContainer)
    // This avoids any timing/caching issues with getGameTableScale()
    let gameScale = 1;
    if (chipAnimLayer) {
      const cssWidth = chipAnimLayer.offsetWidth;
      const visualWidth = chipAnimLayer.getBoundingClientRect().width;
      if (cssWidth > 0 && visualWidth > 0) {
        gameScale = visualWidth / cssWidth;
      }
    }

    // Calculate centers in viewport coordinates
    const sourceCenterX = sourceRect.left + sourceRect.width / 2;
    const sourceCenterY = sourceRect.top + sourceRect.height / 2;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    // Calculate chip size - when using animation layer, chip needs to be scaled up
    // because it will be rendered inside the scaled-down canvas
    const actualChipSize = useContainerRelative ? flyingChipSize / gameScale : flyingChipSize;

    // Position chip at source center
    let startX: number;
    let startY: number;

    // Convert to container-relative coordinates if using animation layer
    if (useContainerRelative && chipAnimLayer) {
      const layerRect = chipAnimLayer.getBoundingClientRect();
      const layerOffsetWidth = chipAnimLayer.offsetWidth;

      // DEBUG: Log all coordinate info
      console.log(`[animateChipFlight DEBUG] value=${value}`, {
        layerRect: { left: layerRect.left.toFixed(1), top: layerRect.top.toFixed(1), width: layerRect.width.toFixed(1) },
        layerOffsetWidth,
        gameScale: gameScale.toFixed(3),
        sourceRect: { left: sourceRect.left.toFixed(1), top: sourceRect.top.toFixed(1), width: sourceRect.width.toFixed(1) },
        targetRect: { left: targetRect.left.toFixed(1), top: targetRect.top.toFixed(1), width: targetRect.width.toFixed(1) },
        sourceCenterX: sourceCenterX.toFixed(1),
        targetCenterX: targetCenterX.toFixed(1),
      });

      // Convert viewport coords to canvas coords (divide by scale)
      startX = (sourceCenterX - layerRect.left) / gameScale - actualChipSize / 2;
      startY = (sourceCenterY - layerRect.top) / gameScale - actualChipSize / 2;
    } else {
      startX = sourceCenterX - flyingChipSize / 2;
      startY = sourceCenterY - flyingChipSize / 2;
    }

    // Calculate translation - in canvas coordinates when using animation layer
    const translateX = (targetCenterX - sourceCenterX) / gameScale;
    const translateY = (targetCenterY - sourceCenterY) / gameScale;

    // Create flying chip element
    // When using animation layer, use actualChipSize (scaled up to compensate for canvas scale)
    const renderChipSize = useContainerRelative ? actualChipSize : flyingChipSize;
    const renderFontSize = renderChipSize * 0.29;
    const renderBorderWidth = renderChipSize * 0.083;

    const chip = document.createElement('div');
    const chipImage = CHIP_IMAGES[value];
    chip.style.cssText = `
      position: ${useContainerRelative ? 'absolute' : 'fixed'};
      box-sizing: border-box;
      left: ${startX}px;
      top: ${startY}px;
      width: ${renderChipSize}px;
      height: ${renderChipSize}px;
      border-radius: 50%;
      ${chipImage ? `
        background-image: url(${chipImage});
        background-size: cover;
        background-position: center;
      ` : `
        border: ${renderBorderWidth}px solid ${colors.border};
        background: ${colors.bg};
      `}
      color: ${colors.text};
      font-weight: bold;
      font-size: ${renderFontSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      z-index: ${zIndex};
      box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
      overflow: hidden;
      will-change: transform, opacity;
      transform: translateZ(0);
      backface-visibility: hidden;
      isolation: isolate;
    `;
    // No denomination label on animated chips

    console.log(`[animateChipFlight] Starting chip ${value} from (${startX.toFixed(0)}, ${startY.toFixed(0)}) translate (${translateX.toFixed(0)}, ${translateY.toFixed(0)}) container=${useContainerRelative} scale=${gameScale.toFixed(2)}`);

    // Append to animation layer if available, otherwise fall back to body
    if (useContainerRelative && chipAnimLayer) {
      chipAnimLayer.appendChild(chip);
    } else {
      document.body.appendChild(chip);
    }

    // Calculate end scale using actual visual target size from targetRect
    // This correctly handles targets inside zoomed containers (like bet stack with zoom: 0.72)
    const actualTargetVisualSize = targetRect.width || targetChipSize;
    const endScale = actualTargetVisualSize / sourceChipSize;

    // Animate
    const animation = chip.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${translateX}px, ${translateY}px) scale(${endScale})`, opacity: 1 },
      ],
      {
        duration,
        easing,
        fill: 'forwards',
      }
    );

    // Play sound at 25% of animation - sound tail overlaps with visual landing
    setTimeout(() => playSound('chipLand', { volume: soundVolume }), duration * 0.25);

    animation.onfinish = () => {
      // Safe removal: hide immediately, remove from DOM after 2 frames
      // This prevents card text re-rasterization ("swell/shrink" effect)
      chip.style.opacity = '0';
      chip.style.pointerEvents = 'none';
      resolve();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chip.remove();
        });
      });
    };

    // Also clean up if animation is cancelled (e.g., navigation away)
    animation.oncancel = () => {
      chip.style.opacity = '0';
      chip.style.pointerEvents = 'none';
      resolve();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chip.remove();
        });
      });
    };
  });
}

/**
 * Container-relative chip flight config
 * Used for opponent avatar↔kitty animations where chips must be in the same stacking context
 */
export interface ContainerChipFlightConfig {
  /** Chip denomination value */
  value: number;
  /** Source bounding rect (viewport coordinates) */
  sourceRect: DOMRect;
  /** Target bounding rect (viewport coordinates) */
  targetRect: DOMRect;
  /** Container element to append chip to (must have position: relative) */
  container: Element;
  /** Container's bounding rect (for coordinate conversion) */
  containerRect: DOMRect;
  /** Starting chip size in pixels */
  sourceChipSize: number;
  /** Ending chip size in pixels */
  targetChipSize: number;
  /** Animation duration in ms (default: 200) */
  duration?: number;
  /** Easing function (default: 'ease-out') */
  easing?: string;
  /** Z-index for the flying chip (default: 25, between seat box z-10 and avatar z-30) */
  zIndex?: number;
  /** Sound volume multiplier 0.0-1.0 (default: 1.0). Use lower values for opponent chips. */
  soundVolume?: number;
}

/**
 * Container-relative chip animation for opponent avatar↔kitty
 * Uses position: absolute within the container instead of position: fixed on body.
 * This ensures proper z-index stacking (chips behind avatar at z-30).
 *
 * IMPORTANT: Compensates for CSS scale transforms on parent elements.
 * getBoundingClientRect() returns scaled viewport coordinates, but position: absolute
 * uses unscaled CSS coordinates. We detect and compensate for any scaling.
 */
export function animateChipFlightInContainer(config: ContainerChipFlightConfig): Promise<void> {
  return new Promise((resolve) => {
    const {
      value,
      sourceRect,
      targetRect,
      container,
      containerRect,
      sourceChipSize,
      targetChipSize,
      duration = 200,
      easing = 'ease-out',
      zIndex = 25,
      soundVolume = 1.0,
    } = config;

    const colors = getChipColors(value);

    // Detect CSS scale transform by comparing rendered size to CSS size
    // offsetWidth/offsetHeight give CSS dimensions, containerRect gives rendered (scaled) dimensions
    const containerEl = container as HTMLElement;
    const cssWidth = containerEl.offsetWidth;
    const cssHeight = containerEl.offsetHeight;
    const scaleX = cssWidth > 0 ? containerRect.width / cssWidth : 1;
    const scaleY = cssHeight > 0 ? containerRect.height / cssHeight : 1;

    // Use the average scale (they should be the same for uniform scaling)
    const scale = (scaleX + scaleY) / 2;

    // Adjust chip size for the scale - we need to use unscaled CSS pixels
    const flyingChipSize = sourceChipSize / scale;
    const fontSize = flyingChipSize * 0.29;
    const borderWidth = flyingChipSize * 0.083;

    // Create flying chip element
    const chip = document.createElement('div');
    const chipImage = CHIP_IMAGES[value];
    chip.style.cssText = `
      position: absolute;
      box-sizing: border-box;
      width: ${flyingChipSize}px;
      height: ${flyingChipSize}px;
      border-radius: 50%;
      ${chipImage ? `
        background-image: url(${chipImage});
        background-size: cover;
        background-position: center;
      ` : `
        border: ${borderWidth}px solid ${colors.border};
        background: ${colors.bg};
      `}
      color: ${colors.text};
      font-weight: bold;
      font-size: ${fontSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      z-index: ${zIndex};
      box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
      overflow: hidden;
      will-change: transform, opacity;
      transform: translateZ(0);
      backface-visibility: hidden;
      isolation: isolate;
    `;
    // No denomination label on animated chips

    // Calculate centers in viewport coordinates
    const sourceCenterX = sourceRect.left + sourceRect.width / 2;
    const sourceCenterY = sourceRect.top + sourceRect.height / 2;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    // Convert from viewport coordinates to container-local CSS coordinates
    // 1. Subtract container's viewport position
    // 2. Divide by scale to convert from scaled to unscaled coordinates
    const startX = (sourceCenterX - containerRect.left) / scale - flyingChipSize / 2;
    const startY = (sourceCenterY - containerRect.top) / scale - flyingChipSize / 2;

    // Calculate translation in unscaled CSS coordinates
    const translateX = (targetCenterX - sourceCenterX) / scale;
    const translateY = (targetCenterY - sourceCenterY) / scale;

    console.log(`[animateChipFlightInContainer] Chip $${value}: scale=${scale.toFixed(3)} container(${containerRect.left.toFixed(0)},${containerRect.top.toFixed(0)} ${containerRect.width.toFixed(0)}x${containerRect.height.toFixed(0)}) css(${cssWidth}x${cssHeight}) source(${sourceCenterX.toFixed(0)},${sourceCenterY.toFixed(0)}) target(${targetCenterX.toFixed(0)},${targetCenterY.toFixed(0)}) startPos(${startX.toFixed(0)},${startY.toFixed(0)}) translate(${translateX.toFixed(0)},${translateY.toFixed(0)})`);

    chip.style.left = `${startX}px`;
    chip.style.top = `${startY}px`;

    container.appendChild(chip);

    // Calculate end scale
    // If source and target chip sizes are the same, skip scaling entirely (e.g., kitty to avatar)
    // Otherwise, use target rect for zoomed container handling (like bet stack with zoom: 0.72)
    let endScale: number;
    if (sourceChipSize === targetChipSize) {
      // No size change requested - keep chips at their original size
      endScale = 1;
    } else {
      // Scale to match target visual size (for zoomed containers)
      const actualTargetVisualSize = targetRect.width || targetChipSize;
      const targetChipSizeCSS = actualTargetVisualSize / scale;
      endScale = targetChipSizeCSS / flyingChipSize;
    }

    // Animate
    const animation = chip.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${translateX}px, ${translateY}px) scale(${endScale})`, opacity: 1 },
      ],
      {
        duration,
        easing,
        fill: 'forwards',
      }
    );

    // Play sound at 25% of animation - sound tail overlaps with visual landing
    setTimeout(() => playSound('chipLand', { volume: soundVolume }), duration * 0.25);

    animation.onfinish = () => {
      // Safe removal: hide immediately, remove from DOM after 2 frames
      // This prevents card text re-rasterization ("swell/shrink" effect)
      chip.style.opacity = '0';
      chip.style.pointerEvents = 'none';
      resolve();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chip.remove();
        });
      });
    };

    // Also clean up if animation is cancelled (e.g., navigation away)
    animation.oncancel = () => {
      chip.style.opacity = '0';
      chip.style.pointerEvents = 'none';
      resolve();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          chip.remove();
        });
      });
    };
  });
}

/**
 * Batch animation config for container-relative animations
 */
export interface ContainerBatchAnimationConfig {
  /** Chips to animate (denomination values) */
  chips: number[];
  /** Function to get source rect for a chip (by value and index) */
  getSourceRect: (value: number, index: number) => DOMRect | null;
  /** Function to get target rect for a chip (by value and index) */
  getTargetRect: (value: number, index: number) => DOMRect | null;
  /** Container element to append chips to */
  container: Element;
  /** Container's bounding rect */
  containerRect: DOMRect;
  /** Source chip size */
  sourceChipSize: number;
  /** Target chip size */
  targetChipSize: number;
  /** Stagger delay between chips (ms) */
  staggerDelay?: number;
  /** Animation duration per chip (ms) */
  duration?: number;
  /** Called when each chip animation starts */
  onChipStart?: (value: number, index: number) => void;
  /** Called when each chip animation ends */
  onChipEnd?: (value: number, index: number) => void;
  /** Z-index for flying chips (default: 25) */
  zIndex?: number;
  /** Sound volume multiplier 0.0-1.0 (default: 1.0). Use lower values for opponent chips. */
  soundVolume?: number;
}

/**
 * Animate a batch of chips within a container with staggered timing
 */
export async function animateChipBatchInContainer(config: ContainerBatchAnimationConfig): Promise<void> {
  const {
    chips,
    getSourceRect,
    getTargetRect,
    container,
    containerRect,
    sourceChipSize,
    targetChipSize,
    staggerDelay = 80,
    duration = 200,
    onChipStart,
    onChipEnd,
    zIndex,
    soundVolume,
  } = config;

  if (chips.length === 0) return;

  // Preserve caller's chip order - callers are responsible for sorting
  // (ascending for arriving chips, descending for leaving/unstacking chips)
  const animations: Promise<void>[] = [];

  chips.forEach((chipValue, index) => {
    const sourceRect = getSourceRect(chipValue, index);
    const targetRect = getTargetRect(chipValue, index);

    if (!sourceRect || !targetRect) {
      console.log(`[animateChipBatchInContainer] Skipping chip ${chipValue}: missing rect`);
      return;
    }

    const animPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        onChipStart?.(chipValue, index);

        animateChipFlightInContainer({
          value: chipValue,
          sourceRect,
          targetRect,
          container,
          containerRect,
          sourceChipSize,
          targetChipSize,
          duration,
          zIndex,
          soundVolume,
        }).then(() => {
          onChipEnd?.(chipValue, index);
          resolve();
        });
      }, index * staggerDelay);
    });

    animations.push(animPromise);
  });

  await Promise.all(animations);
}

// ===========================================
// BATCH ANIMATION HELPER
// ===========================================

export interface BatchAnimationConfig {
  /** Chips to animate (denomination values) */
  chips: number[];
  /** Function to get source rect for a chip (by value and index) */
  getSourceRect: (value: number, index: number) => DOMRect | null;
  /** Function to get target rect for a chip (by value and index) */
  getTargetRect: (value: number, index: number) => DOMRect | null;
  /** Source chip size */
  sourceChipSize: number;
  /** Target chip size */
  targetChipSize: number;
  /** Stagger delay between chips (ms) */
  staggerDelay?: number;
  /** Animation duration per chip (ms) */
  duration?: number;
  /** Called when each chip animation starts */
  onChipStart?: (value: number, index: number) => void;
  /** Called when each chip animation ends */
  onChipEnd?: (value: number, index: number) => void;
  /** Z-index for flying chips (default: 9999). Use lower value to animate behind avatars. */
  zIndex?: number;
  /** Sound volume multiplier 0.0-1.0 (default: 1.0). Use lower values for opponent chips. */
  soundVolume?: number;
}

/**
 * Animate a batch of chips with staggered timing
 * Returns a promise that resolves when all chips have landed
 */
export async function animateChipBatch(config: BatchAnimationConfig): Promise<void> {
  const {
    chips,
    getSourceRect,
    getTargetRect,
    sourceChipSize,
    targetChipSize,
    staggerDelay = 80,
    duration = 200,
    onChipStart,
    onChipEnd,
    zIndex,
    soundVolume,
  } = config;

  if (chips.length === 0) return;

  // Preserve caller's chip order - callers are responsible for sorting
  // (ascending for arriving chips, descending for leaving/unstacking chips)
  const animations: Promise<void>[] = [];

  chips.forEach((chipValue, index) => {
    const sourceRect = getSourceRect(chipValue, index);
    const targetRect = getTargetRect(chipValue, index);

    if (!sourceRect || !targetRect) {
      console.log(`[animateChipBatch] Skipping chip ${chipValue} at index ${index}: sourceRect=${!!sourceRect}, targetRect=${!!targetRect}`);
      return;
    }
    if (sourceRect.width === 0 || targetRect.width === 0) {
      console.log(`[animateChipBatch] Skipping chip ${chipValue} at index ${index}: sourceRect.width=${sourceRect.width}, targetRect.width=${targetRect.width}`);
      return;
    }

    const animPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        onChipStart?.(chipValue, index);

        animateChipFlight({
          value: chipValue,
          sourceRect,
          targetRect,
          sourceChipSize,
          targetChipSize,
          duration,
          zIndex,
          soundVolume,
        }).then(() => {
          onChipEnd?.(chipValue, index);
          resolve();
        });
      }, index * staggerDelay);
    });

    animations.push(animPromise);
  });

  console.log(`[animateChipBatch] Created ${animations.length} animations for ${chips.length} chips`);
  await Promise.all(animations);
}

// ===========================================
// HIGH-LEVEL ANIMATION FUNCTIONS
// ===========================================

/**
 * Animate chips from chip rail to staged bet area
 */
export function animateRailToStaged(config: {
  value: number;
  sourceRect: DOMRect;
  targetRect: DOMRect;
  onComplete?: () => void;
}): Promise<void> {
  const { value, sourceRect, targetRect, onComplete } = config;

  return animateChipFlight({
    value,
    sourceRect,
    targetRect,
    sourceChipSize: sourceRect.width || CHIP_SIZES.rail,
    targetChipSize: targetRect.width || CHIP_SIZES.staged,
    duration: 200,
  }).then(() => {
    onComplete?.();
  });
}

/**
 * Animate chips from staged area back to chip rail (CLEAR button)
 */
export async function animateStagedToRail(config: {
  chips: number[];
  getStagedRect: (denom: number) => DOMRect | null;
  getRailRect: (denom: number) => DOMRect | null;
  onChipStart?: (value: number) => void;
  staggerDelay?: number;
}): Promise<void> {
  const { chips, getStagedRect, getRailRect, onChipStart, staggerDelay = 80 } = config;

  await animateChipBatch({
    chips,
    getSourceRect: (value) => getStagedRect(value),
    getTargetRect: (value) => getRailRect(value),
    sourceChipSize: CHIP_SIZES.staged,
    targetChipSize: CHIP_SIZES.rail,
    staggerDelay,
    duration: 150,
    onChipStart: (value) => {
      onChipStart?.(value);
    },
  });
}

/**
 * Animate chips from staged area to player's kitty (BET button)
 */
export async function animateStagedToKitty(config: {
  chips: number[];
  getStagedRect: (denom: number) => DOMRect | null;
  getKittyRect: (denom: number) => DOMRect | null;
  onChipEnd?: (value: number, index: number) => void;
  staggerDelay?: number;
}): Promise<void> {
  const { chips, getStagedRect, getKittyRect, onChipEnd, staggerDelay = 80 } = config;

  await animateChipBatch({
    chips,
    getSourceRect: (value) => getStagedRect(value),
    getTargetRect: (value) => getKittyRect(value),
    sourceChipSize: CHIP_SIZES.staged,
    targetChipSize: CHIP_SIZES.kittyMedium,
    staggerDelay,
    duration: 250,
    onChipEnd,
  });
}

/**
 * Animate payout chips from dealer pile to player's kitty
 */
export async function animatePayoutToKitty(config: {
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  onChipEnd?: (value: number, index: number) => void;
}): Promise<void> {
  const { chips, handIndex, staggerDelay = 80, onChipEnd } = config;

  // Get dealer pile element
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animatePayoutToKitty] Dealer chip pile not found');
    return;
  }

  // Get player's kitty element
  const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
  if (!kittyElement) {
    console.warn('[animatePayoutToKitty] Player kitty not found');
    return;
  }

  if (chips.length === 0) return;

  const fallbackSourceRect = dealerPile.getBoundingClientRect();
  const kittyRect = kittyElement.getBoundingClientRect();

  // Get unique denominations and find their positions in the kitty
  // If handIndex is provided, target chips for that specific hand
  const uniqueDenoms = [...new Set(chips)].sort((a, b) => a - b);

  // Find tray slot sources for each denomination
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomSourceRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    // Build selector - include hand index if provided
    const handSelector = handIndex !== undefined ? `[data-hand-index="${handIndex}"]` : '';
    const denomChip = kittyElement.querySelector(`[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`);
    if (denomChip) {
      denomTargetRects.set(denom, denomChip.getBoundingClientRect());
    }
  }

  await animateChipBatch({
    chips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || kittyRect,
    sourceChipSize: CHIP_SIZES.dealerPilePop, // 25% larger for visual pop when departing tray
    targetChipSize: CHIP_SIZES.kittyMedium,
    staggerDelay,
    duration: 250,
    onChipEnd,
  });
}

/**
 * Animate chips from player's kitty to chip rail (collecting winnings)
 */
export async function animateKittyToRail(config: {
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  onChipStart?: (value: number, index: number) => void;
  onRectsCaptured?: () => void;
}): Promise<void> {
  const { chips, handIndex, staggerDelay = 80, onChipStart: externalOnChipStart, onRectsCaptured } = config;

  // Get player's kitty element
  const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
  if (!kittyElement) {
    console.warn('[animateKittyToRail] Player kitty not found');
    return;
  }

  if (chips.length === 0) return;

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Build hand selector if handIndex is provided
  const handSelector = handIndex !== undefined ? `[data-hand-index="${handIndex}"]` : '';
  console.log(`[animateKittyToRail] Looking for chips with handSelector: "${handSelector}", uniqueDenoms:`, uniqueDenoms);

  // Capture source positions from kitty chip elements for this specific hand
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`;
    const kittyChip = kittyElement.querySelector(selector);
    if (kittyChip) {
      const rect = kittyChip.getBoundingClientRect();
      denomSourceRects.set(denom, rect);
      console.log(`[animateKittyToRail] Found chip denom ${denom}:`, { left: rect.left.toFixed(0), top: rect.top.toFixed(0), width: rect.width, height: rect.height });
    } else {
      console.log(`[animateKittyToRail] NOT FOUND: ${selector}`);
    }
  }

  const kittyRect = kittyElement.getBoundingClientRect();
  console.log(`[animateKittyToRail] Kitty fallback rect:`, { left: kittyRect.left.toFixed(0), top: kittyRect.top.toFixed(0), width: kittyRect.width, height: kittyRect.height });

  // Capture target positions from chip rail
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const railChip = document.querySelector(`[data-chip-rail="${denom}"]`);
    if (railChip) {
      denomTargetRects.set(denom, railChip.getBoundingClientRect());
    }
  }

  // Notify caller that all rects are captured — safe to hide DOM chips now.
  onRectsCaptured?.();

  // Track chip unstacking - only hide visual chips when remaining count <= MAX_VISIBLE
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  // Map to store which chip index to hide for each animation (keyed by "denom-animIndex")
  const pendingHides = new Map<string, number>();

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || kittyRect,
    getTargetRect: (value) => denomTargetRects.get(value) || null,
    sourceChipSize: CHIP_SIZES.kittyMedium,
    targetChipSize: CHIP_SIZES.rail,
    staggerDelay,
    duration: 200,
    onChipStart: (value, index) => {
      // Capture which chip to hide BEFORE recording animation start
      const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
      if (chipIndexToHide !== null) {
        pendingHides.set(`${value}-${index}`, chipIndexToHide);
      }
      // Record animation start for unstack tracking
      unstackTracker.recordAnimationStart(value);
      // Call external callback for counter updates (decrement as chip starts flying)
      externalOnChipStart?.(value, index);
    },
    onChipEnd: (value, index) => {
      // Hide DOM chip AFTER flying chip animation completes
      const chipIndexToHide = pendingHides.get(`${value}-${index}`);
      if (chipIndexToHide !== undefined) {
        const chipToHide = kittyElement.querySelector(
          `[data-chip-denom="${value}"]${handSelector}[data-chip-index="${chipIndexToHide}"]`
        );
        if (chipToHide) {
          (chipToHide as HTMLElement).style.opacity = '0';
        }
        pendingHides.delete(`${value}-${index}`);
      }
    },
  });
}

/**
 * Animate chips from player's kitty to dealer pile (losing hand)
 */
export async function animateKittyToDealer(config: {
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  onChipStart?: (value: number, index: number) => void;
  onRectsCaptured?: () => void;
}): Promise<void> {
  const { chips, handIndex, staggerDelay = 80, onChipStart: externalOnChipStart, onRectsCaptured } = config;

  // Get dealer pile element
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animateKittyToDealer] Dealer chip pile not found');
    return;
  }

  // Get player's kitty element
  const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
  if (!kittyElement) {
    console.warn('[animateKittyToDealer] Player kitty not found');
    return;
  }

  if (chips.length === 0) return;

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Build hand selector if handIndex is provided
  const handSelector = handIndex !== undefined ? `[data-hand-index="${handIndex}"]` : '';
  console.log(`[animateKittyToDealer] Looking for chips with handSelector: "${handSelector}", uniqueDenoms:`, uniqueDenoms);

  // Debug: Log kitty element's parent chain to understand layout
  const kittyRect = kittyElement.getBoundingClientRect();
  console.log(`[animateKittyToDealer] Kitty rect:`, {
    left: kittyRect.left.toFixed(0),
    top: kittyRect.top.toFixed(0),
    width: kittyRect.width.toFixed(0),
    height: kittyRect.height.toFixed(0),
  });

  // Log parent element position (PlayerSeat container)
  const parentEl = kittyElement.parentElement;
  if (parentEl) {
    const parentRect = parentEl.getBoundingClientRect();
    console.log(`[animateKittyToDealer] Parent rect:`, {
      left: parentRect.left.toFixed(0),
      top: parentRect.top.toFixed(0),
      width: parentRect.width.toFixed(0),
    });
  }

  // Capture source positions from kitty chip elements for this specific hand
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`;
    const kittyChip = kittyElement.querySelector(selector);
    if (kittyChip) {
      const rect = kittyChip.getBoundingClientRect();
      denomSourceRects.set(denom, rect);
      console.log(`[animateKittyToDealer] Found chip denom ${denom}:`, { left: rect.left.toFixed(0), top: rect.top.toFixed(0), width: rect.width, height: rect.height });
    } else {
      console.log(`[animateKittyToDealer] NOT FOUND: ${selector}`);
    }
  }
  const fallbackTargetRect = dealerPile.getBoundingClientRect();

  // Find tray slot targets for each denomination
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomTargetRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  // Notify caller that all rects are captured — safe to hide DOM chips now.
  // WAAPI flying chips will be the only visible representation during animation.
  onRectsCaptured?.();

  // Track chip unstacking - only hide visual chips when remaining count <= MAX_VISIBLE
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  // Map to store which chip index to hide for each animation (keyed by "denom-animIndex")
  const pendingHides = new Map<string, number>();

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || kittyRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    sourceChipSize: CHIP_SIZES.kittyMedium,
    targetChipSize: CHIP_SIZES.dealerPile,
    staggerDelay,
    duration: 250,
    onChipStart: (value, index) => {
      // Capture which chip to hide BEFORE recording animation start
      const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
      if (chipIndexToHide !== null) {
        pendingHides.set(`${value}-${index}`, chipIndexToHide);
      }
      // Record animation start for unstack tracking
      unstackTracker.recordAnimationStart(value);
      // Call external callback for counter updates (decrement as chip starts flying)
      externalOnChipStart?.(value, index);
    },
    onChipEnd: (value, index) => {
      // Hide DOM chip AFTER flying chip animation completes
      const chipIndexToHide = pendingHides.get(`${value}-${index}`);
      if (chipIndexToHide !== undefined) {
        const chipToHide = kittyElement.querySelector(
          `[data-chip-denom="${value}"]${handSelector}[data-chip-index="${chipIndexToHide}"]`
        );
        if (chipToHide) {
          (chipToHide as HTMLElement).style.opacity = '0';
        }
        pendingHides.delete(`${value}-${index}`);
      }
    },
  });
}

// ===========================================
// INSURANCE ANIMATION FUNCTIONS
// ===========================================

/**
 * Animate chips from chip rail to insurance kitty (buying insurance)
 */
export async function animateRailToInsuranceKitty(config: {
  chips: number[];
  staggerDelay?: number;
  onChipEnd?: (value: number, index: number) => void;
}): Promise<void> {
  const { chips, staggerDelay = 80, onChipEnd } = config;

  console.log('[animateRailToInsuranceKitty] Starting with chips:', chips);

  // Get player's kitty element to find insurance stack within it
  const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
  if (!kittyElement) {
    console.warn('[animateRailToInsuranceKitty] Player kitty not found');
    return;
  }

  // Wait for insurance kitty element to appear (React needs time to render it)
  const insuranceKitty = await waitForElement('[data-player-kitty="current-user"] [data-insurance-kitty="true"]', 2000);
  if (!insuranceKitty) {
    console.warn('[animateRailToInsuranceKitty] Insurance kitty not found after waiting');
    return;
  }
  console.log('[animateRailToInsuranceKitty] Found insurance kitty element');

  if (chips.length === 0) {
    console.warn('[animateRailToInsuranceKitty] No chips to animate');
    return;
  }

  const sortedChips = [...chips].sort((a, b) => a - b);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Capture source positions from chip rail
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const railChip = document.querySelector(`[data-chip-rail="${denom}"]`);
    if (railChip) {
      const rect = railChip.getBoundingClientRect();
      denomSourceRects.set(denom, rect);
      console.log(`[animateRailToInsuranceKitty] Found rail chip for denom ${denom}, rect:`, {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      });
    } else {
      console.warn(`[animateRailToInsuranceKitty] Rail chip NOT found for denom ${denom}`);
    }
  }

  if (denomSourceRects.size === 0) {
    console.warn('[animateRailToInsuranceKitty] No rail chips found at all - cannot animate');
    return;
  }

  // Target is the insurance kitty
  const targetRect = insuranceKitty.getBoundingClientRect();
  console.log('[animateRailToInsuranceKitty] Target rect:', targetRect);

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || null,
    getTargetRect: () => targetRect,
    sourceChipSize: CHIP_SIZES.rail,
    targetChipSize: CHIP_SIZES.kittyMedium,
    staggerDelay,
    duration: 250,
    onChipEnd,
  });

  console.log('[animateRailToInsuranceKitty] Animation complete');
}

/**
 * Animate chips from chip rail to player's main kitty (for double down)
 */
export async function animateRailToKitty(config: {
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  onChipEnd?: (value: number, index: number) => void;
}): Promise<void> {
  const { chips, handIndex = 0, staggerDelay = 80, onChipEnd } = config;

  console.log('[animateRailToKitty] Starting with chips:', chips, 'handIndex:', handIndex);

  // Get player's kitty element
  const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
  if (!kittyElement) {
    console.warn('[animateRailToKitty] Player kitty not found');
    return;
  }

  if (chips.length === 0) {
    console.warn('[animateRailToKitty] No chips to animate');
    return;
  }

  const sortedChips = [...chips].sort((a, b) => a - b);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Capture source positions from chip rail
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const railChip = document.querySelector(`[data-chip-rail="${denom}"]`);
    if (railChip) {
      const rect = railChip.getBoundingClientRect();
      denomSourceRects.set(denom, rect);
    }
  }

  if (denomSourceRects.size === 0) {
    console.warn('[animateRailToKitty] No rail chips found - cannot animate');
    return;
  }

  // Get fallback kitty rect
  const kittyRect = kittyElement.getBoundingClientRect();

  // Build target rects for each denomination - target specific chips in the stack
  // This ensures each chip lands on its matching denomination position
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    // Build selector with hand index to target the correct split hand's chips
    const handSelector = `[data-hand-index="${handIndex}"]`;
    const denomChip = kittyElement.querySelector(`[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`);
    if (denomChip) {
      denomTargetRects.set(denom, denomChip.getBoundingClientRect());
      console.log(`[animateRailToKitty] Found target for denom ${denom} at hand ${handIndex}`);
    }
  }

  console.log('[animateRailToKitty] Target rects found:', denomTargetRects.size, 'for', uniqueDenoms.length, 'denominations');

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || null,
    getTargetRect: (value) => denomTargetRects.get(value) || kittyRect,
    sourceChipSize: CHIP_SIZES.rail,
    targetChipSize: CHIP_SIZES.kittyMedium,
    staggerDelay,
    duration: 250,
    onChipEnd,
  });

  console.log('[animateRailToKitty] Animation complete');
}

/**
 * Animate insurance payout from dealer pile to insurance kitty (when insurance wins)
 */
export async function animateInsurancePayoutToKitty(config: {
  chips: number[];
  /** Pre-captured target rect (optional - avoids DOM query race condition) */
  targetRect?: DOMRect;
  staggerDelay?: number;
  onChipEnd?: (value: number, index: number) => void;
}): Promise<void> {
  const { chips, targetRect: preTargetRect, staggerDelay = 80, onChipEnd } = config;

  // Get dealer pile element
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animateInsurancePayoutToKitty] Dealer chip pile not found');
    return;
  }

  if (chips.length === 0) return;

  const fallbackSourceRect = dealerPile.getBoundingClientRect();

  // Find tray slot sources for each denomination
  const uniqueDenoms = [...new Set(chips)].sort((a, b) => a - b);
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomSourceRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  // Use pre-captured rect if provided, otherwise query DOM
  let targetRect = preTargetRect;
  if (!targetRect) {
    const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
    if (!kittyElement) {
      console.warn('[animateInsurancePayoutToKitty] Player kitty not found');
      return;
    }
    let insuranceKitty = kittyElement.querySelector('[data-insurance-kitty="true"]');
    if (!insuranceKitty) {
      insuranceKitty = await waitForElement('[data-player-kitty="current-user"] [data-insurance-kitty="true"]', 2000);
    }
    if (!insuranceKitty) {
      console.warn('[animateInsurancePayoutToKitty] Insurance kitty not found');
      return;
    }
    targetRect = insuranceKitty.getBoundingClientRect();
  }

  await animateChipBatch({
    chips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: () => targetRect!,
    sourceChipSize: CHIP_SIZES.dealerPilePop, // 25% larger for visual pop when departing tray
    targetChipSize: CHIP_SIZES.kittyMedium,
    staggerDelay,
    duration: 250,
    onChipEnd,
  });
}

/**
 * Animate chips from insurance kitty to chip rail (collecting insurance winnings)
 */
export async function animateInsuranceKittyToRail(config: {
  chips: number[];
  /** Pre-captured source rect (optional - avoids DOM query race condition) */
  sourceRect?: DOMRect;
  staggerDelay?: number;
  onChipStart?: (value: number, index: number) => void;
}): Promise<void> {
  const { chips, sourceRect: preSourceRect, staggerDelay = 80, onChipStart: externalOnChipStart } = config;

  if (chips.length === 0) return;

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Use pre-captured rect if provided, otherwise query DOM
  let sourceRect = preSourceRect;
  let insuranceKitty: Element | null = null;
  if (!sourceRect) {
    const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
    if (!kittyElement) {
      console.warn('[animateInsuranceKittyToRail] Player kitty not found');
      return;
    }
    insuranceKitty = kittyElement.querySelector('[data-insurance-kitty="true"]');
    if (!insuranceKitty) {
      insuranceKitty = await waitForElement('[data-player-kitty="current-user"] [data-insurance-kitty="true"]', 2000);
    }
    if (!insuranceKitty) {
      console.warn('[animateInsuranceKittyToRail] Insurance kitty not found');
      return;
    }
    sourceRect = insuranceKitty.getBoundingClientRect();
  }

  // Capture target positions from chip rail
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const railChip = document.querySelector(`[data-chip-rail="${denom}"]`);
    if (railChip) {
      denomTargetRects.set(denom, railChip.getBoundingClientRect());
    }
  }

  // Track chip unstacking - only hide visual chips when remaining count <= MAX_VISIBLE
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: () => sourceRect!,
    getTargetRect: (value) => denomTargetRects.get(value) || null,
    sourceChipSize: CHIP_SIZES.kittyMedium,
    targetChipSize: CHIP_SIZES.rail,
    staggerDelay,
    duration: 200,
    onChipStart: (value, index) => {
      // Only hide visual chip when remaining count drops to MAX_VISIBLE or below
      if (insuranceKitty) {
        const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
        if (chipIndexToHide !== null) {
          const chipToHide = insuranceKitty.querySelector(
            `[data-chip-denom="${value}"][data-chip-index="${chipIndexToHide}"]`
          );
          if (chipToHide) {
            (chipToHide as HTMLElement).style.opacity = '0';
          }
        }
      }
      unstackTracker.recordAnimationStart(value);

      externalOnChipStart?.(value, index);
    },
  });
}

/**
 * Animate chips from insurance kitty to dealer pile (losing insurance bet)
 */
export async function animateInsuranceKittyToDealer(config: {
  chips: number[];
  /** Pre-captured source rect (optional - avoids DOM query race condition) */
  sourceRect?: DOMRect;
  staggerDelay?: number;
  onChipStart?: (value: number, index: number) => void;
}): Promise<void> {
  const { chips, sourceRect: preSourceRect, staggerDelay = 80, onChipStart: externalOnChipStart } = config;

  // Get dealer pile element
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animateInsuranceKittyToDealer] Dealer chip pile not found');
    return;
  }

  if (chips.length === 0) return;

  // Use pre-captured rect if provided, otherwise query DOM
  let sourceRect = preSourceRect;
  let insuranceKitty: Element | null = null;
  if (!sourceRect) {
    const kittyElement = document.querySelector('[data-player-kitty="current-user"]');
    if (!kittyElement) {
      console.warn('[animateInsuranceKittyToDealer] Player kitty not found');
      return;
    }
    insuranceKitty = kittyElement.querySelector('[data-insurance-kitty="true"]');
    if (!insuranceKitty) {
      insuranceKitty = await waitForElement('[data-player-kitty="current-user"] [data-insurance-kitty="true"]', 2000);
    }
    if (!insuranceKitty) {
      console.warn('[animateInsuranceKittyToDealer] Insurance kitty not found');
      return;
    }
    sourceRect = insuranceKitty.getBoundingClientRect();
  }

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const fallbackTargetRect = dealerPile.getBoundingClientRect();

  // Find tray slot targets for each denomination
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomTargetRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  // Track chip unstacking - only hide visual chips when remaining count <= MAX_VISIBLE
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: () => sourceRect!,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    sourceChipSize: CHIP_SIZES.kittyMedium,
    targetChipSize: CHIP_SIZES.dealerPile,
    staggerDelay,
    duration: 200,
    onChipStart: (value, index) => {
      // Only hide visual chip when remaining count drops to MAX_VISIBLE or below
      if (insuranceKitty) {
        const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
        if (chipIndexToHide !== null) {
          const chipToHide = insuranceKitty.querySelector(
            `[data-chip-denom="${value}"][data-chip-index="${chipIndexToHide}"]`
          );
          if (chipToHide) {
            (chipToHide as HTMLElement).style.opacity = '0';
          }
        }
      }
      unstackTracker.recordAnimationStart(value);

      externalOnChipStart?.(value, index);
    },
  });
}

// ===========================================
// CHIP SWAP ANIMATION (CONSOLIDATION)
// ===========================================

/**
 * Animate a chip denomination swap: old chips fly from staged → rail,
 * new consolidated chips fly from rail → staged, simultaneously.
 */
export async function animateChipSwap(config: {
  removedChips: number[];
  addedChips: number[];
  getStagedRect: (denom: number) => DOMRect | null;
  getRailRect: (denom: number) => DOMRect | null;
}): Promise<void> {
  const { removedChips, addedChips, getStagedRect, getRailRect } = config;

  if (removedChips.length === 0 && addedChips.length === 0) return;

  // Phase 1: old chips fly staged → rail (fast, staggered)
  const outPromise = removedChips.length > 0
    ? animateChipBatch({
        chips: removedChips,
        getSourceRect: (value) => getStagedRect(value),
        getTargetRect: (value) => getRailRect(value),
        sourceChipSize: CHIP_SIZES.staged,
        targetChipSize: CHIP_SIZES.rail,
        staggerDelay: 60,
        duration: 150,
      })
    : Promise.resolve();

  // Phase 2: new chips fly rail → staged, starts 100ms after first "out" chip
  const inPromise = addedChips.length > 0
    ? new Promise<void>((resolve) => {
        setTimeout(() => {
          animateChipBatch({
            chips: addedChips,
            getSourceRect: (value) => getRailRect(value),
            getTargetRect: (value) => getStagedRect(value),
            sourceChipSize: CHIP_SIZES.rail,
            targetChipSize: CHIP_SIZES.staged,
            staggerDelay: 60,
            duration: 200,
          }).then(resolve);
        }, 100);
      })
    : Promise.resolve();

  await Promise.all([outPromise, inPromise]);
}

// ===========================================
// OPPONENT CHIP ANIMATIONS
// ===========================================

/**
 * Animate chips from opponent's avatar to their insurance kitty (when opponent buys insurance)
 * Uses container-relative positioning for proper z-index stacking (chips behind avatar)
 */
export async function animateOpponentInsuranceToKitty(config: {
  seatPosition: number;
  chips: number[];
  staggerDelay?: number;
}): Promise<void> {
  const { seatPosition, chips, staggerDelay = 60 } = config;

  if (chips.length === 0) return;

  // Get opponent's seat container for proper z-index stacking
  const container = document.querySelector(`[data-opponent-seat="${seatPosition}"]`);
  if (!container) {
    console.warn(`[animateOpponentInsuranceToKitty] Seat container not found for seat ${seatPosition}`);
    return;
  }
  const containerRect = container.getBoundingClientRect();

  // Get opponent's avatar (source)
  const avatarElement = document.querySelector(`[data-opponent-avatar="${seatPosition}"]`);
  if (!avatarElement) {
    console.warn(`[animateOpponentInsuranceToKitty] Avatar not found for seat ${seatPosition}`);
    return;
  }

  // Get opponent's kitty, then find insurance kitty within it
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentInsuranceToKitty] Kitty not found for seat ${seatPosition}`);
    return;
  }

  // Wait for insurance kitty element to appear (React may need time to render)
  let insuranceKitty = kittyElement.querySelector('[data-insurance-kitty="true"]');
  if (!insuranceKitty) {
    // Wait up to 500ms for element to appear
    insuranceKitty = await waitForElement(`[data-opponent-kitty="${seatPosition}"] [data-insurance-kitty="true"]`, 500);
  }
  if (!insuranceKitty) {
    console.warn(`[animateOpponentInsuranceToKitty] Insurance kitty not found for seat ${seatPosition}`);
    return;
  }

  const sourceRect = avatarElement.getBoundingClientRect();

  // Sort chips and find unique denominations
  const sortedChips = [...chips].sort((a, b) => a - b);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Find denomination-specific target positions in the insurance kitty
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"][data-chip-index="0"]`;
    const kittyChip = insuranceKitty.querySelector(selector);
    if (kittyChip) {
      denomTargetRects.set(denom, kittyChip.getBoundingClientRect());
    }
  }

  const fallbackTargetRect = insuranceKitty.getBoundingClientRect();

  // Pre-hide all chips in the insurance kitty (they'll be revealed one by one as animation chips land)
  const allInsuranceChips = insuranceKitty.querySelectorAll('[data-chip-denom]');
  allInsuranceChips.forEach((chip) => {
    (chip as HTMLElement).style.opacity = '0';
  });

  // Track how many chips of each denomination we've revealed
  const revealedPerDenom = new Map<number, number>();

  // Count total chips per denomination for proper indexing
  const chipsPerDenom = new Map<number, number>();
  for (const chip of sortedChips) {
    chipsPerDenom.set(chip, (chipsPerDenom.get(chip) || 0) + 1);
  }

  await animateChipBatchInContainer({
    chips: sortedChips,
    getSourceRect: () => sourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    container,
    containerRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.kittySmall,
    staggerDelay,
    duration: 200,
    zIndex: 25, // Above seat box (z-10) and cards, but below avatar (z-30)
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipEnd: (value) => {
      // Reveal the next chip of this denomination
      const alreadyRevealed = revealedPerDenom.get(value) || 0;
      const totalForDenom = chipsPerDenom.get(value) || 0;

      if (alreadyRevealed < totalForDenom) {
        const chipIndexToReveal = alreadyRevealed;
        const chipToReveal = insuranceKitty!.querySelector(
          `[data-chip-denom="${value}"][data-chip-index="${chipIndexToReveal}"]`
        );
        if (chipToReveal) {
          (chipToReveal as HTMLElement).style.opacity = '1';
          console.log(`[animateOpponentInsuranceToKitty] Revealed chip denom ${value} index ${chipIndexToReveal}`);
        }
      }

      revealedPerDenom.set(value, alreadyRevealed + 1);
    },
  });
}

/**
 * Animate chips from opponent's avatar to their kitty (when opponent places bet)
 * Uses container-relative positioning for proper z-index stacking (chips behind avatar)
 */
export async function animateOpponentBetToKitty(config: {
  seatPosition: number;
  chips: number[];
  staggerDelay?: number;
}): Promise<void> {
  const { seatPosition, chips, staggerDelay = 60 } = config;

  if (chips.length === 0) return;

  // Get opponent's seat container for proper z-index stacking
  const container = document.querySelector(`[data-opponent-seat="${seatPosition}"]`);
  if (!container) {
    console.warn(`[animateOpponentBetToKitty] Seat container not found for seat ${seatPosition}`);
    return;
  }
  const containerRect = container.getBoundingClientRect();

  // Get opponent's avatar (source)
  const avatarElement = document.querySelector(`[data-opponent-avatar="${seatPosition}"]`);
  if (!avatarElement) {
    console.warn(`[animateOpponentBetToKitty] Avatar not found for seat ${seatPosition}`);
    return;
  }

  // Get opponent's kitty (target)
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentBetToKitty] Kitty not found for seat ${seatPosition}`);
    return;
  }

  const sourceRect = avatarElement.getBoundingClientRect();

  // Sort chips and find unique denominations
  const sortedChips = [...chips].sort((a, b) => a - b);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Find denomination-specific target positions in the kitty
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"][data-chip-index="0"]`;
    const kittyChip = kittyElement.querySelector(selector);
    if (kittyChip) {
      denomTargetRects.set(denom, kittyChip.getBoundingClientRect());
    }
  }

  const fallbackTargetRect = kittyElement.getBoundingClientRect();

  // Pre-hide all chips in the kitty (they'll be revealed one by one as animation chips land)
  const allKittyChips = kittyElement.querySelectorAll('[data-chip-denom]');
  allKittyChips.forEach((chip) => {
    (chip as HTMLElement).style.opacity = '0';
  });

  // Track how many chips of each denomination we've revealed
  const revealedPerDenom = new Map<number, number>();

  // Count total chips per denomination for proper indexing
  const chipsPerDenom = new Map<number, number>();
  for (const chip of sortedChips) {
    chipsPerDenom.set(chip, (chipsPerDenom.get(chip) || 0) + 1);
  }

  // Create DOM-based counter that increments as chips land
  // (React counter is hidden via startHidden, this provides progressive count)
  // Match styling from ChipStack's counter pill (small size: 10px font, pill bg)
  let runningTotal = 0;
  const counterElement = document.createElement('div');
  // Apply all styles synchronously before appending to avoid any timing issues
  counterElement.style.position = 'absolute';
  counterElement.style.left = '50%';
  counterElement.style.transform = 'translateX(-50%)';
  counterElement.style.bottom = '0';
  counterElement.style.fontSize = '10px';
  counterElement.style.fontWeight = 'bold';
  counterElement.style.color = '#FB923C';
  counterElement.style.whiteSpace = 'nowrap';
  counterElement.style.pointerEvents = 'none';
  counterElement.style.zIndex = '100';
  counterElement.style.padding = '2px 8px';
  counterElement.style.borderRadius = '9999px';
  counterElement.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
  counterElement.style.transition = 'none';
  counterElement.textContent = formatBalance(0);
  kittyElement.appendChild(counterElement);

  await animateChipBatchInContainer({
    chips: sortedChips,
    getSourceRect: () => sourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    container,
    containerRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.kittySmall,
    staggerDelay,
    duration: 200,
    zIndex: 25, // Above seat box (z-10) and cards, but below avatar (z-30)
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipEnd: (value) => {
      // Increment running total and update counter display
      runningTotal += value;
      counterElement.textContent = formatBalance(runningTotal);

      // Reveal the next chip of this denomination
      const alreadyRevealed = revealedPerDenom.get(value) || 0;
      const totalForDenom = chipsPerDenom.get(value) || 0;

      if (alreadyRevealed < totalForDenom) {
        // Calculate which chip index to reveal (reveal from bottom to top)
        const chipIndexToReveal = alreadyRevealed;
        const chipToReveal = kittyElement.querySelector(
          `[data-chip-denom="${value}"][data-chip-index="${chipIndexToReveal}"]`
        );
        if (chipToReveal) {
          (chipToReveal as HTMLElement).style.opacity = '1';
        }
      }

      revealedPerDenom.set(value, alreadyRevealed + 1);
    },
  });

  // Remove DOM counter - React counter will now be visible (startHidden becomes false)
  counterElement.remove();

  // Reveal the React counter by finding and showing the data-chip-amount element
  const reactCounter = kittyElement.querySelector('[data-chip-amount]') as HTMLElement;
  if (reactCounter) {
    reactCounter.style.opacity = '1';
  }
}

/**
 * Animate chips from opponent's avatar to their kitty for a split or double bet
 * Used when opponent splits (new hand) or doubles (adding to existing hand)
 * Uses container-relative positioning for proper z-index stacking (chips behind avatar)
 * @param isNewHand - true for SPLIT (hide all and reveal), false for DOUBLE (no hiding, just animate)
 */
export async function animateOpponentSplitBetToKitty(config: {
  seatPosition: number;
  handIndex: number;
  chips: number[];
  staggerDelay?: number;
  isNewHand?: boolean;
}): Promise<void> {
  const { seatPosition, handIndex, chips, staggerDelay = 60, isNewHand = true } = config;

  if (chips.length === 0) return;

  // Get opponent's seat container for proper z-index stacking
  const container = document.querySelector(`[data-opponent-seat="${seatPosition}"]`);
  if (!container) {
    console.warn(`[animateOpponentSplitBetToKitty] Seat container not found for seat ${seatPosition}`);
    return;
  }
  const containerRect = container.getBoundingClientRect();

  // Get opponent's avatar (source)
  const avatarElement = document.querySelector(`[data-opponent-avatar="${seatPosition}"]`);
  if (!avatarElement) {
    console.warn(`[animateOpponentSplitBetToKitty] Avatar not found for seat ${seatPosition}`);
    return;
  }

  // Get opponent's kitty
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentSplitBetToKitty] Kitty not found for seat ${seatPosition}`);
    return;
  }

  const sourceRect = avatarElement.getBoundingClientRect();

  // Try to find the specific hand's chip stack for targeting
  const sortedChips = [...chips].sort((a, b) => a - b);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);
  const handSelector = `[data-hand-index="${handIndex}"]`;

  // Find denomination-specific target positions
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`;
    const chip = kittyElement.querySelector(selector);
    if (chip) {
      denomTargetRects.set(denom, chip.getBoundingClientRect());
    }
  }

  // Fallback to kitty center if chips not found
  const fallbackTargetRect = kittyElement.getBoundingClientRect();

  // For new hands (SPLIT), pre-hide all chips and reveal progressively
  // For adding to existing (DOUBLE), skip hiding - let animation just run on top
  const newChipsPerDenom = new Map<number, number>();
  const domChipsPerDenom = new Map<number, number>();

  if (isNewHand) {
    // Count chips per denomination
    for (const chip of sortedChips) {
      newChipsPerDenom.set(chip, (newChipsPerDenom.get(chip) || 0) + 1);
    }

    // Count TOTAL DOM chips per denomination for this hand
    for (const denom of uniqueDenoms) {
      const chips = kittyElement.querySelectorAll(`[data-chip-denom="${denom}"]${handSelector}`);
      domChipsPerDenom.set(denom, chips.length);
    }

    // Pre-hide all chips for this new hand
    for (const denom of uniqueDenoms) {
      const totalDom = domChipsPerDenom.get(denom) || 0;
      for (let i = 0; i < totalDom; i++) {
        const chip = kittyElement.querySelector(
          `[data-chip-denom="${denom}"]${handSelector}[data-chip-index="${i}"]`
        );
        if (chip) {
          (chip as HTMLElement).style.opacity = '0';
        }
      }
    }
  }

  // Track how many chips of each denomination we've revealed (only used for new hands)
  const revealedPerDenom = new Map<number, number>();

  // Create DOM-based counter for new split hands (React counter is hidden via startHidden)
  let runningTotal = 0;
  let counterElement: HTMLElement | null = null;
  if (isNewHand) {
    counterElement = document.createElement('div');
    // Apply all styles synchronously before appending to avoid any timing issues
    counterElement.style.position = 'absolute';
    counterElement.style.left = '50%';
    counterElement.style.transform = 'translateX(-50%)';
    counterElement.style.bottom = '0';
    counterElement.style.fontSize = '10px';
    counterElement.style.fontWeight = 'bold';
    counterElement.style.color = '#FB923C';
    counterElement.style.whiteSpace = 'nowrap';
    counterElement.style.pointerEvents = 'none';
    counterElement.style.zIndex = '100';
    counterElement.style.padding = '2px 8px';
    counterElement.style.borderRadius = '9999px';
    counterElement.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    counterElement.style.transition = 'none';
    counterElement.textContent = formatBalance(0);
    kittyElement.appendChild(counterElement);
  }

  await animateChipBatchInContainer({
    chips: sortedChips,
    getSourceRect: () => sourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    container,
    containerRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.kittySmall,
    staggerDelay,
    duration: 200,
    zIndex: 25, // Above seat box (z-10) and cards, but below avatar (z-30)
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipEnd: isNewHand ? (value) => {
      // Increment running total and update counter display
      runningTotal += value;
      if (counterElement) {
        counterElement.textContent = formatBalance(runningTotal);
      }

      // Reveal the next chip of this denomination for this new hand
      const alreadyRevealed = revealedPerDenom.get(value) || 0;
      const totalDom = domChipsPerDenom.get(value) || 0;

      if (alreadyRevealed < totalDom) {
        const chipIndexToReveal = alreadyRevealed;
        const chipToReveal = kittyElement.querySelector(
          `[data-chip-denom="${value}"]${handSelector}[data-chip-index="${chipIndexToReveal}"]`
        );
        if (chipToReveal) {
          (chipToReveal as HTMLElement).style.opacity = '1';
        }
      }

      revealedPerDenom.set(value, alreadyRevealed + 1);
    } : undefined, // For DOUBLE (not new hand), no reveal needed - chips are already visible
  });

  // Remove DOM counter and reveal React counter for new hands
  if (counterElement) {
    counterElement.remove();
    const reactCounter = kittyElement.querySelector(`[data-chip-amount="${handIndex}"]`) as HTMLElement;
    if (reactCounter) {
      reactCounter.style.opacity = '1';
    }
  }
}

/**
 * Animate payout chips from dealer pile to opponent's kitty (when opponent wins)
 * Step 1 of 2: Dealer → Kitty
 * Chips fly from denomination-specific tray slots and land at denomination-specific kitty positions.
 * For denominations not in the original bet, calculates where they WOULD land in the merged stack.
 */
export async function animatePayoutToOpponentKitty(config: {
  seatPosition: number;
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  duration?: number;
}): Promise<void> {
  const { seatPosition, chips, handIndex = 0, staggerDelay = 60, duration = 250 } = config;

  if (chips.length === 0) return;

  // Get dealer pile (source)
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animatePayoutToOpponentKitty] Dealer chip pile not found');
    return;
  }

  // Get opponent's kitty (target)
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animatePayoutToOpponentKitty] Kitty not found for seat ${seatPosition}`);
    return;
  }

  const sortedChips = [...chips].sort((a, b) => a - b);
  const payoutDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Find tray slot sources for each denomination
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of payoutDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomSourceRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  // Build hand selector for targeting specific hand's chips
  const handSelector = `[data-hand-index="${handIndex}"]`;

  // Collect ALL existing chip positions and denominations in the kitty (bet chips)
  const existingChips = kittyElement.querySelectorAll(`[data-chip-denom]${handSelector}[data-chip-index="0"]`);
  const existingDenomPositions = new Map<number, DOMRect>();
  const existingDenoms: number[] = [];

  for (const chip of existingChips) {
    const denom = parseInt((chip as HTMLElement).dataset.chipDenom || '0', 10);
    if (denom > 0) {
      existingDenomPositions.set(denom, chip.getBoundingClientRect());
      existingDenoms.push(denom);
    }
  }

  // ChipStack layout constants (small size for opponents)
  const STACK_OFFSET = 6; // pixels between denomination layers (small size)
  const CHIP_SIZE = CHIP_SIZES.kittySmall; // 20px

  // Merge existing (bet) denominations with payout denominations
  const allDenoms = [...new Set([...existingDenoms, ...payoutDenoms])].sort((a, b) => b - a); // Sort descending like ChipStack
  const totalGroups = allDenoms.length;

  // Find a reference chip position to anchor calculations
  // Use the lowest denomination chip (bottom of stack) as baseline
  let baselineRect: DOMRect | null = null;
  let baselineDenom = 0;

  // Find lowest existing denomination
  const sortedExistingDenoms = [...existingDenoms].sort((a, b) => a - b);
  if (sortedExistingDenoms.length > 0) {
    baselineDenom = sortedExistingDenoms[0];
    baselineRect = existingDenomPositions.get(baselineDenom) || null;
  }

  // Build target rects for all payout denominations
  const denomTargetRects = new Map<number, DOMRect>();

  for (const denom of payoutDenoms) {
    // If this denomination already exists in the kitty, use its position
    const existingPos = existingDenomPositions.get(denom);
    if (existingPos) {
      denomTargetRects.set(denom, existingPos);
      continue;
    }

    // Calculate position for new denomination based on merged stack layout
    if (baselineRect) {
      // Find where this denomination sits in the sorted (descending) stack
      const denomIndex = allDenoms.indexOf(denom);
      const baselineIndex = allDenoms.indexOf(baselineDenom);

      // Calculate Y offset from baseline
      // Higher index = lower in stack (higher denominations at top)
      // ChipStack positions: bottom: (totalGroups - 1 - groupIdx) * STACK_OFFSET
      // So moving from baseline to new denom: deltaY = (baselineIndex - denomIndex) * STACK_OFFSET
      const deltaY = (baselineIndex - denomIndex) * STACK_OFFSET;

      // Create a synthetic rect at the calculated position
      const syntheticRect = new DOMRect(
        baselineRect.x,
        baselineRect.y - deltaY, // Negative because higher = smaller Y (toward top of screen)
        baselineRect.width,
        baselineRect.height
      );

      denomTargetRects.set(denom, syntheticRect);
      console.log(`[animatePayoutToOpponentKitty] Calculated position for denom ${denom}: Y offset ${-deltaY}px from baseline ${baselineDenom}`);
    }
  }

  const fallbackSourceRect = dealerPile.getBoundingClientRect();
  const fallbackTargetRect = kittyElement.getBoundingClientRect();

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    sourceChipSize: CHIP_SIZES.dealerPilePop, // 25% larger for visual pop when departing tray
    targetChipSize: CHIP_SIZES.kittySmall,
    staggerDelay,
    duration,
    soundVolume: OPPONENT_SOUND_VOLUME,
  });
}

/**
 * Animate chips from opponent's kitty to opponent's avatar (when opponent wins)
 * Step 2 of 2: Kitty → Avatar
 * Uses container-relative positioning for proper z-index stacking (chips behind avatar)
 * Chips fly from denomination-specific positions and decrement visually as they animate.
 */
export async function animateOpponentKittyToAvatar(config: {
  seatPosition: number;
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  duration?: number;
}): Promise<void> {
  const { seatPosition, chips, handIndex = 0, staggerDelay = 60, duration = 250 } = config;

  if (chips.length === 0) return;

  // NOTE: Do NOT clear card clones here - they need to persist until rake animation
  // runs at BET_CONFIRMED. Card clones have pointer-events: none so they don't
  // interfere with chip animations.

  // Remove force-visible CSS rule from bet animation - it has !important which overrides our opacity:0
  const forceVisibleRule = document.querySelector(`style[data-force-visible-opponent-bet-${seatPosition}]`);
  if (forceVisibleRule) {
    forceVisibleRule.remove();
    console.log(`[animateOpponentKittyToAvatar] Removed force-visible rule for seat ${seatPosition}`);
  }

  // Use the chip animation layer (z-45) so chips animate above cards (z-40)
  const container = document.querySelector('[data-chip-animation-layer]');
  if (!container) {
    console.warn(`[animateOpponentKittyToAvatar] Chip animation layer not found`);
    return;
  }
  const containerRect = container.getBoundingClientRect();

  // Get opponent's kitty (source)
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentKittyToAvatar] Kitty not found for seat ${seatPosition}`);
    return;
  }

  // Get opponent's avatar (target)
  const avatarElement = document.querySelector(`[data-opponent-avatar="${seatPosition}"]`);
  if (!avatarElement) {
    console.warn(`[animateOpponentKittyToAvatar] Avatar not found for seat ${seatPosition}`);
    return;
  }

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Build hand selector if handIndex is provided
  const handSelector = `[data-hand-index="${handIndex}"]`;

  // Capture source positions from kitty chip elements for each denomination
  // Try with handIndex first, then without as fallback (for single-hand opponents)
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    // First try with handIndex selector
    let selector = `[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`;
    let kittyChip = kittyElement.querySelector(selector);

    // If not found, try without handIndex (for single-hand cases or different attribute format)
    if (!kittyChip) {
      selector = `[data-chip-denom="${denom}"][data-chip-index="0"]`;
      kittyChip = kittyElement.querySelector(selector);
    }

    if (kittyChip) {
      const rect = kittyChip.getBoundingClientRect();
      denomSourceRects.set(denom, rect);
      console.log(`[animateOpponentKittyToAvatar] Found denom ${denom} at (${rect.left.toFixed(0)}, ${rect.top.toFixed(0)})`);
    } else {
      console.log(`[animateOpponentKittyToAvatar] Denom ${denom} NOT FOUND in kitty for seat ${seatPosition}, hand ${handIndex}`);
    }
  }

  const fallbackSourceRect = kittyElement.getBoundingClientRect();
  const targetRect = avatarElement.getBoundingClientRect();

  console.log(`[animateOpponentKittyToAvatar] Found ${denomSourceRects.size}/${uniqueDenoms.length} denom positions, fallback: (${fallbackSourceRect.left.toFixed(0)}, ${fallbackSourceRect.top.toFixed(0)})`);

  // Count actual visible DOM chips per denomination (not animation chips)
  // This is important because payout chips are animated but don't exist in DOM
  // IMPORTANT: Only count chips for THIS hand to avoid hiding chips from other split hands
  const domChipCounts = new Map<number, number>();
  for (const denom of uniqueDenoms) {
    // Count only chips of this denomination for THIS hand
    const selector = `[data-chip-denom="${denom}"]${handSelector}`;
    const chips = kittyElement.querySelectorAll(selector);
    domChipCounts.set(denom, chips.length);
  }

  // Build chip array based on DOM counts for proper unstacking
  const domChips: number[] = [];
  for (const [denom, count] of domChipCounts) {
    for (let i = 0; i < count; i++) {
      domChips.push(denom);
    }
  }

  console.log(`[animateOpponentKittyToAvatar] DOM chip counts:`, Object.fromEntries(domChipCounts), 'total DOM chips:', domChips.length, 'animation chips:', sortedChips.length);

  // Track chip unstacking based on actual DOM chips, not animation chips
  const unstackTracker = new ChipUnstackTracker(domChips);

  // Track how many chips we've animated per denomination for hiding logic
  const animatedPerDenom = new Map<number, number>();

  await animateChipBatchInContainer({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: () => targetRect,
    container,
    containerRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.kittySmall,
    staggerDelay,
    duration,
    zIndex: 100, // Above cards and seat box, visible during flight to avatar
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipStart: (value) => {
      const domCount = domChipCounts.get(value) || 0;
      const alreadyAnimated = animatedPerDenom.get(value) || 0;

      // Only try to hide if there are still DOM chips to hide for this denomination
      if (alreadyAnimated < domCount) {
        // Get the chip index to hide based on DOM chip count
        const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
        if (chipIndexToHide !== null) {
          // IMPORTANT: Only hide chips for THIS hand - no fallback to avoid hiding other hands' chips
          const chipToHide = kittyElement.querySelector(
            `[data-chip-denom="${value}"]${handSelector}[data-chip-index="${chipIndexToHide}"]`
          );
          if (chipToHide) {
            (chipToHide as HTMLElement).style.opacity = '0';
            console.log(`[animateOpponentKittyToAvatar] Hiding chip denom ${value} hand ${handIndex} index ${chipIndexToHide}`);
          }
        }
        unstackTracker.recordAnimationStart(value);
      }

      animatedPerDenom.set(value, alreadyAnimated + 1);
    },
  });
}

/**
 * Animate chips from opponent's kitty to dealer pile (when opponent loses)
 * Chips fly from denomination-specific positions and decrement visually as they animate.
 */
export async function animateOpponentLossToDealer(config: {
  seatPosition: number;
  chips: number[];
  handIndex?: number;
  staggerDelay?: number;
  duration?: number;
}): Promise<void> {
  const { seatPosition, chips, handIndex = 0, staggerDelay = 60, duration = 250 } = config;

  if (chips.length === 0) return;

  // NOTE: Do NOT clear card clones here - they need to persist until rake animation
  // runs at BET_CONFIRMED. Card clones have pointer-events: none so they don't
  // interfere with chip animations.

  // Remove force-visible CSS rule from bet animation - it has !important which overrides our opacity:0
  const forceVisibleRule = document.querySelector(`style[data-force-visible-opponent-bet-${seatPosition}]`);
  if (forceVisibleRule) {
    forceVisibleRule.remove();
    console.log(`[animateOpponentLossToDealer] Removed force-visible rule for seat ${seatPosition}`);
  }

  // Get opponent's kitty (source)
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentLossToDealer] Kitty not found for seat ${seatPosition}`);
    return;
  }

  // Get dealer pile (target)
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animateOpponentLossToDealer] Dealer chip pile not found');
    return;
  }

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Build hand selector if handIndex is provided
  const handSelector = `[data-hand-index="${handIndex}"]`;

  // Capture source positions from kitty chip elements for each denomination
  // Try with handIndex first, then without as fallback (for single-hand opponents)
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    // First try with handIndex selector
    let selector = `[data-chip-denom="${denom}"]${handSelector}[data-chip-index="0"]`;
    let kittyChip = kittyElement.querySelector(selector);

    // If not found, try without handIndex (for single-hand cases)
    if (!kittyChip) {
      selector = `[data-chip-denom="${denom}"][data-chip-index="0"]`;
      kittyChip = kittyElement.querySelector(selector);
    }

    if (kittyChip) {
      const rect = kittyChip.getBoundingClientRect();
      denomSourceRects.set(denom, rect);
      console.log(`[animateOpponentLossToDealer] Found denom ${denom} at (${rect.left.toFixed(0)}, ${rect.top.toFixed(0)})`);
    } else {
      console.log(`[animateOpponentLossToDealer] Denom ${denom} NOT FOUND in kitty for seat ${seatPosition}, hand ${handIndex}`);
    }
  }

  const fallbackSourceRect = kittyElement.getBoundingClientRect();

  // Find tray slot targets for each denomination
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomTargetRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  const fallbackTargetRect = dealerPile.getBoundingClientRect();

  console.log(`[animateOpponentLossToDealer] Found ${denomSourceRects.size}/${uniqueDenoms.length} denom positions`);

  // Track chip unstacking - only hide visual chips when remaining count <= MAX_VISIBLE
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.dealerPile,
    staggerDelay,
    duration,
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipStart: (value) => {
      // Only hide visual chip when remaining count drops to MAX_VISIBLE or below
      const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
      if (chipIndexToHide !== null) {
        // IMPORTANT: Only hide chips for THIS hand - no fallback to avoid hiding other hands' chips
        const chipToHide = kittyElement.querySelector(
          `[data-chip-denom="${value}"]${handSelector}[data-chip-index="${chipIndexToHide}"]`
        );
        if (chipToHide) {
          (chipToHide as HTMLElement).style.opacity = '0';
        }
      }
      unstackTracker.recordAnimationStart(value);
    },
  });
}

// ===========================================
// OPPONENT INSURANCE RESOLUTION
// ===========================================

/**
 * Animate chips from opponent's insurance kitty to dealer pile (when opponent loses insurance)
 */
export async function animateOpponentInsuranceLossToDealer(config: {
  seatPosition: number;
  chips: number[];
  staggerDelay?: number;
  duration?: number;
}): Promise<void> {
  const { seatPosition, chips, staggerDelay = 60, duration = 250 } = config;

  if (chips.length === 0) return;

  // NOTE: Do NOT clear card clones here - they persist until rake at BET_CONFIRMED

  // Get opponent's kitty
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentInsuranceLossToDealer] Kitty not found for seat ${seatPosition}`);
    return;
  }

  // Get insurance kitty within opponent's kitty
  const insuranceKitty = kittyElement.querySelector('[data-insurance-kitty="true"]');
  if (!insuranceKitty) {
    console.warn(`[animateOpponentInsuranceLossToDealer] Insurance kitty not found for seat ${seatPosition}`);
    return;
  }

  // Get dealer pile (target)
  const dealerPile = document.querySelector('[data-dealer-chip-pile]');
  if (!dealerPile) {
    console.warn('[animateOpponentInsuranceLossToDealer] Dealer chip pile not found');
    return;
  }

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Capture source positions from insurance kitty chip elements
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"][data-chip-index="0"]`;
    const kittyChip = insuranceKitty.querySelector(selector);
    if (kittyChip) {
      denomSourceRects.set(denom, kittyChip.getBoundingClientRect());
    }
  }

  const fallbackSourceRect = insuranceKitty.getBoundingClientRect();

  // Find tray slot targets for each denomination
  const denomTargetRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const traySlot = dealerPile.querySelector(`[data-tray-slot="${denom}"]`);
    if (traySlot) {
      denomTargetRects.set(denom, traySlot.getBoundingClientRect());
    }
  }

  const fallbackTargetRect = dealerPile.getBoundingClientRect();

  // Track chip unstacking
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  await animateChipBatch({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: (value) => denomTargetRects.get(value) || fallbackTargetRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.dealerPile,
    staggerDelay,
    duration,
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipStart: (value) => {
      const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
      if (chipIndexToHide !== null) {
        const chipToHide = insuranceKitty.querySelector(
          `[data-chip-denom="${value}"][data-chip-index="${chipIndexToHide}"]`
        );
        if (chipToHide) {
          (chipToHide as HTMLElement).style.opacity = '0';
        }
      }
      unstackTracker.recordAnimationStart(value);
    },
  });
}

/**
 * Animate chips from opponent's insurance kitty to opponent's avatar (when opponent wins insurance)
 * Uses container-relative positioning for proper z-index stacking (chips behind avatar)
 */
export async function animateOpponentInsuranceKittyToAvatar(config: {
  seatPosition: number;
  chips: number[];
  staggerDelay?: number;
  duration?: number;
}): Promise<void> {
  const { seatPosition, chips, staggerDelay = 60, duration = 250 } = config;

  if (chips.length === 0) return;

  // NOTE: Do NOT clear card clones here - they persist until rake at BET_CONFIRMED

  // Get opponent's seat container for proper z-index stacking
  const container = document.querySelector(`[data-opponent-seat="${seatPosition}"]`);
  if (!container) {
    console.warn(`[animateOpponentInsuranceKittyToAvatar] Seat container not found for seat ${seatPosition}`);
    return;
  }
  const containerRect = container.getBoundingClientRect();

  // Get opponent's kitty
  const kittyElement = document.querySelector(`[data-opponent-kitty="${seatPosition}"]`);
  if (!kittyElement) {
    console.warn(`[animateOpponentInsuranceKittyToAvatar] Kitty not found for seat ${seatPosition}`);
    return;
  }

  // Get insurance kitty within opponent's kitty
  const insuranceKitty = kittyElement.querySelector('[data-insurance-kitty="true"]');
  if (!insuranceKitty) {
    console.warn(`[animateOpponentInsuranceKittyToAvatar] Insurance kitty not found for seat ${seatPosition}`);
    return;
  }

  // Get opponent's avatar (target)
  const avatarElement = document.querySelector(`[data-opponent-avatar="${seatPosition}"]`);
  if (!avatarElement) {
    console.warn(`[animateOpponentInsuranceKittyToAvatar] Avatar not found for seat ${seatPosition}`);
    return;
  }

  // Sort descending for leaving/unstack animation (largest chips first)
  const sortedChips = [...chips].sort((a, b) => b - a);
  const uniqueDenoms = [...new Set(sortedChips)].sort((a, b) => a - b);

  // Capture source positions from insurance kitty chip elements
  const denomSourceRects = new Map<number, DOMRect>();
  for (const denom of uniqueDenoms) {
    const selector = `[data-chip-denom="${denom}"][data-chip-index="0"]`;
    const kittyChip = insuranceKitty.querySelector(selector);
    if (kittyChip) {
      denomSourceRects.set(denom, kittyChip.getBoundingClientRect());
    }
  }

  const fallbackSourceRect = insuranceKitty.getBoundingClientRect();
  const targetRect = avatarElement.getBoundingClientRect();

  // Track chip unstacking
  const unstackTracker = new ChipUnstackTracker(sortedChips);

  await animateChipBatchInContainer({
    chips: sortedChips,
    getSourceRect: (value) => denomSourceRects.get(value) || fallbackSourceRect,
    getTargetRect: () => targetRect,
    container,
    containerRect,
    sourceChipSize: CHIP_SIZES.kittySmall,
    targetChipSize: CHIP_SIZES.kittySmall,
    staggerDelay,
    duration,
    zIndex: 25, // Above seat box (z-10) and cards, but below avatar (z-30)
    soundVolume: OPPONENT_SOUND_VOLUME,
    onChipStart: (value) => {
      const chipIndexToHide = unstackTracker.getChipIndexToHide(value);
      if (chipIndexToHide !== null) {
        const chipToHide = insuranceKitty.querySelector(
          `[data-chip-denom="${value}"][data-chip-index="${chipIndexToHide}"]`
        );
        if (chipToHide) {
          (chipToHide as HTMLElement).style.opacity = '0';
        }
      }
      unstackTracker.recordAnimationStart(value);
    },
  });
}

// ===========================================
// DOM HELPERS
// ===========================================

/**
 * Wait for an element to appear in the DOM
 * Returns the element or null if timeout
 */
export function waitForElement(
  selector: string,
  timeout: number = 3000
): Promise<Element | null> {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const check = () => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        resolve(null);
        return;
      }

      requestAnimationFrame(check);
    };

    check();
  });
}

/**
 * Wait for elements matching a selector to have a minimum count
 */
export function waitForElements(
  containerSelector: string,
  childSelector: string,
  minCount: number = 1,
  timeout: number = 3000
): Promise<NodeListOf<Element> | null> {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const check = () => {
      const container = document.querySelector(containerSelector);
      if (container) {
        const elements = container.querySelectorAll(childSelector);
        if (elements.length >= minCount) {
          resolve(elements);
          return;
        }
      }

      if (Date.now() - startTime >= timeout) {
        resolve(null);
        return;
      }

      requestAnimationFrame(check);
    };

    check();
  });
}
